import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Calendar, MapPin, Trash2, Pencil, Plus, LogOut, Loader2, Mail, X, Inbox, CalendarDays,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/adminApi';

const EMPTY = { title: '', venue: '', city: '', date: '', type: 'Club', ticketUrl: '' };
const TYPES = ['Festival', 'Club', 'Tardeo', 'Corporativo', 'Fiesta'];

function EventForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <form
      data-testid="event-form"
      onSubmit={(e) => { e.preventDefault(); onSave(form); }}
      className="border border-acid/40 bg-ink p-5 grid grid-cols-1 sm:grid-cols-2 gap-4"
    >
      <div className="sm:col-span-2">
        <label className="block text-[10px] uppercase tracking-widest text-[#f1f0ea]/50 mb-1">Título</label>
        <input data-testid="event-title-input" required value={form.title} onChange={set('title')}
          className="w-full bg-surface border border-white/15 px-3 py-2 text-[#f1f0ea] focus:border-acid outline-none" />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-widest text-[#f1f0ea]/50 mb-1">Lugar / Sala</label>
        <input data-testid="event-venue-input" required value={form.venue} onChange={set('venue')}
          className="w-full bg-surface border border-white/15 px-3 py-2 text-[#f1f0ea] focus:border-acid outline-none" />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-widest text-[#f1f0ea]/50 mb-1">Ciudad</label>
        <input data-testid="event-city-input" required value={form.city} onChange={set('city')}
          className="w-full bg-surface border border-white/15 px-3 py-2 text-[#f1f0ea] focus:border-acid outline-none" />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-widest text-[#f1f0ea]/50 mb-1">Fecha</label>
        <input data-testid="event-date-input" required type="date" value={form.date} onChange={set('date')}
          className="w-full bg-surface border border-white/15 px-3 py-2 text-[#f1f0ea] focus:border-acid outline-none" />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-widest text-[#f1f0ea]/50 mb-1">Tipo</label>
        <input data-testid="event-type-input" required list="event-types" value={form.type} onChange={set('type')}
          className="w-full bg-surface border border-white/15 px-3 py-2 text-[#f1f0ea] focus:border-acid outline-none" />
        <datalist id="event-types">{TYPES.map((t) => <option key={t} value={t} />)}</datalist>
      </div>
      <div className="sm:col-span-2">
        <label className="block text-[10px] uppercase tracking-widest text-[#f1f0ea]/50 mb-1">URL de entradas (opcional)</label>
        <input data-testid="event-ticket-input" value={form.ticketUrl} onChange={set('ticketUrl')}
          className="w-full bg-surface border border-white/15 px-3 py-2 text-[#f1f0ea] focus:border-acid outline-none" placeholder="https://..." />
      </div>
      <div className="sm:col-span-2 flex gap-3 pt-1">
        <button data-testid="event-save-btn" type="submit" disabled={saving}
          className="bg-acid text-white font-heading uppercase px-6 py-2.5 hover:-translate-y-0.5 transition-transform disabled:opacity-60 flex items-center gap-2">
          {saving ? <Loader2 className="animate-spin" size={16} /> : 'Guardar'}
        </button>
        <button data-testid="event-cancel-btn" type="button" onClick={onCancel}
          className="border border-white/20 text-[#f1f0ea] font-heading uppercase px-6 py-2.5 hover:border-acid transition-colors">
          Cancelar
        </button>
      </div>
    </form>
  );
}

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('events');
  const [events, setEvents] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadEvents = useCallback(() => {
    return api.get('/events').then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, []);
  const loadContacts = useCallback(() => {
    return api.get('/contact').then((r) => setContacts(r.data)).catch(() => setContacts([]));
  }, []);

  useEffect(() => {
    Promise.all([loadEvents(), loadContacts()]).finally(() => setLoading(false));
  }, [loadEvents, loadContacts]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const saveEvent = async (form) => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/events/${editing.id}`, form);
        toast.success('Evento actualizado');
      } else {
        await api.post('/events', form);
        toast.success('Evento creado');
      }
      await loadEvents();
      setShowForm(false);
      setEditing(null);
    } catch (e) {
      toast.error('No se pudo guardar el evento');
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try {
      await api.delete(`/events/${id}`);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      toast.success('Evento eliminado');
    } catch { toast.error('No se pudo eliminar'); }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('¿Eliminar este mensaje?')) return;
    try {
      await api.delete(`/contact/${id}`);
      setContacts((prev) => prev.filter((c) => c.id !== id));
      toast.success('Mensaje eliminado');
    } catch { toast.error('No se pudo eliminar'); }
  };

  const startCreate = () => { setEditing(null); setShowForm(true); };
  const startEdit = (ev) => { setEditing(ev); setShowForm(true); };

  const sortedEvents = events.slice().sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="min-h-screen bg-ink text-[#f1f0ea]" data-testid="admin-panel">
      <header className="border-b border-white/10 sticky top-0 z-20 bg-ink/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-heading uppercase text-xl leading-none">Panel · Alfredj</h1>
            <p className="text-xs text-[#f1f0ea]/50 mt-1">{user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-[#f1f0ea]/60 hover:text-acid transition-colors hidden sm:inline">Ver web</a>
            <button data-testid="logout-btn" onClick={handleLogout}
              className="flex items-center gap-2 border border-white/20 px-4 py-2 text-sm hover:border-acid hover:text-acid transition-colors">
              <LogOut size={15} /> Salir
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-5 flex gap-1">
          <button data-testid="tab-events" onClick={() => setTab('events')}
            className={`flex items-center gap-2 px-4 py-3 text-sm uppercase tracking-wide font-heading border-b-2 transition-colors ${tab === 'events' ? 'border-acid text-acid' : 'border-transparent text-[#f1f0ea]/50 hover:text-[#f1f0ea]'}`}>
            <CalendarDays size={16} /> Eventos <span className="text-xs opacity-60">({events.length})</span>
          </button>
          <button data-testid="tab-contacts" onClick={() => setTab('contacts')}
            className={`flex items-center gap-2 px-4 py-3 text-sm uppercase tracking-wide font-heading border-b-2 transition-colors ${tab === 'contacts' ? 'border-acid text-acid' : 'border-transparent text-[#f1f0ea]/50 hover:text-[#f1f0ea]'}`}>
            <Inbox size={16} /> Mensajes <span className="text-xs opacity-60">({contacts.length})</span>
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        {loading && (
          <div className="flex items-center gap-3 text-[#f1f0ea]/60 py-16 justify-center">
            <Loader2 className="animate-spin" /> Cargando…
          </div>
        )}
        {!loading && tab === 'events' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading uppercase text-2xl">Próximos eventos</h2>
              {!showForm && (
                <button data-testid="add-event-btn" onClick={startCreate}
                  className="flex items-center gap-2 bg-acid text-white font-heading uppercase px-5 py-2.5 hover:-translate-y-0.5 transition-transform">
                  <Plus size={17} /> Nuevo evento
                </button>
              )}
            </div>

            {showForm && (
              <EventForm
                initial={editing ? { title: editing.title, venue: editing.venue, city: editing.city, date: editing.date, type: editing.type, ticketUrl: editing.ticketUrl || '' } : null}
                onSave={saveEvent}
                onCancel={() => { setShowForm(false); setEditing(null); }}
                saving={saving}
              />
            )}

            {sortedEvents.length === 0 ? (
              <p className="text-[#f1f0ea]/50 py-8" data-testid="admin-events-empty">No hay eventos. Crea el primero.</p>
            ) : (
              <div className="border border-white/10 divide-y divide-white/10">
                {sortedEvents.map((ev) => (
                  <div key={ev.id} data-testid={`admin-event-${ev.id}`} className="flex items-center gap-4 p-4 hover:bg-white/[0.02]">
                    <div className="flex flex-col items-center justify-center border border-white/15 w-14 h-14 shrink-0">
                      <span className="font-heading text-lg leading-none">{ev.date.split('-')[2]}</span>
                      <span className="text-[9px] uppercase text-acid tracking-widest">{ev.date.split('-')[1]}/{ev.date.split('-')[0].slice(2)}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="inline-block text-[9px] uppercase tracking-widest bg-acid/15 text-acid px-2 py-0.5 mb-1">{ev.type}</span>
                      <p className="font-heading uppercase text-lg leading-none truncate">{ev.title}</p>
                      <p className="text-sm text-[#f1f0ea]/60 flex items-center gap-1.5 mt-1">
                        <MapPin size={13} className="text-acid" /> {ev.venue} · {ev.city}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button data-testid={`edit-event-${ev.id}`} onClick={() => startEdit(ev)}
                        className="p-2 border border-white/15 hover:border-acid hover:text-acid transition-colors"><Pencil size={15} /></button>
                      <button data-testid={`delete-event-${ev.id}`} onClick={() => deleteEvent(ev.id)}
                        className="p-2 border border-white/15 hover:border-red-500 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {!loading && tab === 'contacts' && (
          <div className="space-y-6">
            <h2 className="font-heading uppercase text-2xl">Solicitudes de contacto</h2>
            {contacts.length === 0 ? (
              <p className="text-[#f1f0ea]/50 py-8" data-testid="admin-contacts-empty">Aún no hay mensajes.</p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {contacts.map((c) => (
                  <div key={c.id} data-testid={`admin-contact-${c.id}`} className="border border-white/10 bg-surface p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-heading uppercase text-lg leading-none">{c.name}</p>
                        <span className="inline-block text-[9px] uppercase tracking-widest bg-acid/15 text-acid px-2 py-0.5 mt-2">{c.eventType}</span>
                      </div>
                      <button data-testid={`delete-contact-${c.id}`} onClick={() => deleteContact(c.id)}
                        className="p-1.5 text-[#f1f0ea]/40 hover:text-red-400 transition-colors"><X size={16} /></button>
                    </div>
                    <div className="mt-3 space-y-1 text-sm text-[#f1f0ea]/70">
                      {c.date && <p className="flex items-center gap-1.5"><Calendar size={13} className="text-acid" /> {c.date}</p>}
                      {c.location && <p className="flex items-center gap-1.5"><MapPin size={13} className="text-acid" /> {c.location}</p>}
                    </div>
                    <p className="mt-3 text-sm text-[#f1f0ea]/80 border-l-2 border-acid pl-3">{c.message}</p>
                    <div className="mt-3 flex items-center justify-between text-xs text-[#f1f0ea]/40">
                      <span className="flex items-center gap-1.5">
                        <Mail size={12} /> {c.emailSent ? 'Email enviado' : 'Solo guardado'}
                      </span>
                      <span>{new Date(c.createdAt).toLocaleDateString('es-ES')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
